import { useLayoutEffect, useRef, useState } from 'react';
import { ImperativePanelHandle } from 'react-resizable-panels';

/**
 * Collapse-sync for the right-stack cards (Agent Cart / POS Cart) — the vertical
 * twin of useLeftStackCollapse. Each card's chevron drives its open flag; each
 * vertical panel's own collapse/expand callbacks keep the flag in sync when the
 * divider is dragged. Returns the open flags + setters (for the cards) and the
 * panel refs (for the Panels).
 *
 * useLayoutEffect (not useEffect): apply the panel collapse/expand in the SAME
 * paint as the card's open/closed render, so the accordion doesn't read as a
 * laggy two-phase animation.
 */
const useRightStackCollapse = () => {
  const [agentOpen, setAgentOpen] = useState(true);
  // POS Cart starts OPEN at its default height (the layout default is [75, 25]);
  // useStackLayout restores the saved split over this when one exists. The agent
  // can still collapse it via the chevron, but that view state isn't persisted —
  // the height preference is what's honoured on the next load. The panel's
  // onExpand/onCollapse callbacks keep this flag in sync with the agent's choice.
  const [posOpen, setPosOpen] = useState(true);
  const agentPanelRef = useRef<ImperativePanelHandle>(null);
  const posPanelRef = useRef<ImperativePanelHandle>(null);

  useLayoutEffect(() => {
    const panel = agentPanelRef.current;
    if (!panel) return;
    if (agentOpen && panel.isCollapsed()) panel.expand();
    else if (!agentOpen && panel.isExpanded()) panel.collapse();
  }, [agentOpen]);
  useLayoutEffect(() => {
    const panel = posPanelRef.current;
    if (!panel) return;
    if (posOpen && panel.isCollapsed()) panel.expand();
    else if (!posOpen && panel.isExpanded()) panel.collapse();
  }, [posOpen]);

  return {
    agentOpen,
    setAgentOpen,
    posOpen,
    setPosOpen,
    agentPanelRef,
    posPanelRef,
  };
};

export default useRightStackCollapse;
